import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Carrier } from '@app/services/carriers/carrier.model';
import { CheckoutService } from '@pages/checkout-app/checkout.service';
import { FieldsTypes, internationalMethods, ShippingMethod } from '@pages/checkout-app/types/types';
import {
  carrierProviderDisplayNameMap,
  CarrierProvidersNames,
  CarrierTypeNames,
} from '@pdqdev/pdq-shared/dist/CarrierData';
import { CarrierProviders, CarrierProviderServices } from '@pdqdev/pdq-shared/dist/entities';
import { DropdownOption } from '@storybook/components/dropdown/dropdown.component';
import { Observable, Subject, Subscription } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { FormService } from '../../../services';
import { fieldsConfig, Options, ShipmentExtras, ShopSettings } from '../../../types';
import { Shop } from '@services/shops/shop.model';

export const LIVE_RATES_SUPPORTED_CARRIER_TYPE_NAME = [
  CarrierTypeNames.SHIPPO,
  CarrierTypeNames.SHIPPO_MA_DOMESTIC,
  CarrierTypeNames.UPS_ABSTRACT,
  CarrierTypeNames.FEDEX_ABSTRACT,
  CarrierTypeNames.ROYAL_MAIL_UK_ABSTRACT,
  CarrierTypeNames.EASYPOST,
  CarrierTypeNames.EASYSHIP,
  CarrierTypeNames.EASYPOST_MA,
  CarrierTypeNames.EASYSHIP_MA_INTERNATIONAL,
  CarrierTypeNames.EASYPOST,
  CarrierTypeNames.EASYPOST_MA,
  CarrierTypeNames.EASYPOST_MA_UPS,
  CarrierTypeNames.EASYPOST_MA_USPS,
  CarrierTypeNames.EASYPOST_MA_APC,
  CarrierTypeNames.UPS_ABSTRACT,
  CarrierTypeNames.FEDEX_ABSTRACT,
  CarrierTypeNames.ROYAL_MAIL_UK_ABSTRACT,
  CarrierTypeNames.DOORDASH,
  CarrierTypeNames.DOORDASH_MA,
  CarrierTypeNames.DOORDASH_DASHLINK,
  CarrierTypeNames.DHL_ECOMMERCE_ABSTRACT,
  CarrierTypeNames.GOOTEN,
  CarrierTypeNames.ATS,
  CarrierTypeNames.FLAVOR_CLOUD,
  CarrierTypeNames.PDQ_SHIPPING_P2PG,
  CarrierTypeNames.UNIUNI,
  CarrierTypeNames.PASSPORT,
  CarrierTypeNames.ARAMEX,
  CarrierTypeNames.GLOBALE,
];

const DOMESTIC_LIVE_RATES_METHOD_TYPES = [ShippingMethod.DOMESTIC_LIVE_RATE, ShippingMethod.FIXED_LIVE_RATE];

@Component({
  selector: 'app-new-recommended-delivery',
  templateUrl: './new-recommended-delivery.html',
  styleUrls: ['./new-recommended-delivery.scss'],
  standalone: false,
})
export class NewRecommendedDelivery implements OnInit, OnChanges, OnDestroy {
  @Input() formGroup: UntypedFormGroup;
  @Input() shipmentExtrasFormGroup: UntypedFormGroup;
  @Input() deliveryType: string;
  @Input() shippingMethodCategory: ShippingMethod;
  @Input() showFormErrors: boolean;
  @Input() loadedCarrier: Carrier;
  @Input() shipmentExtrasValues: ShipmentExtras;
  @Input() shopSettings: ShopSettings;
  @Input() shop: Shop;
  // the saved extras finished loading; edit pages take them as part of the saved values
  @Output() shipmentExtrasLoaded = new EventEmitter<void>();
  DOMESTIC_LIVE_RATES_METHOD_TYPES = DOMESTIC_LIVE_RATES_METHOD_TYPES;
  fieldsTypes = FieldsTypes;
  private formResetSubscription$: Subscription;
  private destroy$ = new Subject();

  // shipment extras
  shipmentExtrasFields: fieldsConfig[] = [];
  isAdvancedSettingsDisplayed: boolean = true;

  // carrier
  carrierOptions$: Observable<DropdownOption[]>;
  defaultCarrierOption: DropdownOption;
  activeCarrier: Carrier = null;
  carriers: Carrier[] = [];

  // providers data;
  selectedActiveProviders: CarrierProvidersNames[] | string[] = [];
  activeProviderServicesIds: string[] = [];
  activeProviderIds: string[] = [];
  arrowFunctionArray: any[] = [];

  constructor(
    private formService: FormService,
    private checkoutService: CheckoutService,
  ) {}

  ngOnInit() {
    this.loadCarrierOptions();
    this.formResetSubscription$ = this.checkoutService.onFormReset$.subscribe((toReset) => {
      if (toReset) {
        this.resetForm();
      }
    });
  }

  ngOnDestroy() {
    this.formResetSubscription$?.unsubscribe();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private resetForm(): void {
    this.shipmentExtrasFormGroup.reset();
    this.isAdvancedSettingsDisplayed = false;
    this.selectedActiveProviders = [];
    this.loadedCarrier = null;
    this.activeCarrier = null;
    this.arrowFunctionArray = [];
    this.activeProviderServicesIds = [];
    this.activeProviderIds = [];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      this.loadCarrierOptions();
    }

    if (this.loadedCarrier) {
      // edit mode
      this.activeCarrier = { ...this.loadedCarrier };
      this.formGroup['controls']['carrierId']?.setValue(this.activeCarrier.id);
      this.shipmentExtrasFields = this.activeCarrier['shipmentExtraFields'];
      this.defaultCarrierOption = { label: this.activeCarrier.name, value: this.activeCarrier.id };
      this.shipmentExtrasFields ? this.initShipmentExtrasFields(this.shipmentExtrasValues) : this.fetchExtraFields();
    }
  }
  isInternationalMethod() {
    return internationalMethods.includes(this.shippingMethodCategory as ShippingMethod);
  }
  loadCarrierOptions() {
    this.carrierOptions$ = this.formService
      .carrierAutoComplete('', { supportsInternationalShipping: this.isInternationalMethod() })
      .pipe(
        map(({ results }) => {
          this.carriers = results;
          const carriers = results.filter(
            (carrier) => LIVE_RATES_SUPPORTED_CARRIER_TYPE_NAME.includes(carrier.type.name) && carrier.isActive,
          );
          return carriers.map((carrier) => {
            return { label: carrier.name, value: carrier.id };
          });
        }),
      );
  }

  onCarrierSelected(event) {
    if (!event || event.value == this.activeCarrier?.id) {
      return;
    }

    this.loadedCarrier = null;
    this.activeCarrier = this.carriers.find((option) => option.id == event.value);
    this.shipmentExtrasFields = this.activeCarrier['shipmentExtraFields'];
    this.checkoutService.setSelectedCarrier(this.activeCarrier);
    this.formGroup['controls']['carrierId']?.setValue(this.activeCarrier.id);
    this.onCarrierChangeResetInternationalSettings();
    this.formGroup['controls']['carrierId'].markAsPristine();
    this.defaultCarrierOption = { label: this.activeCarrier.name, value: this.activeCarrier.id };
    this.showFormErrors = false;
    this.shipmentExtrasFields ? this.initShipmentExtrasFields() : this.fetchExtraFields();
  }

  onCarrierChangeResetInternationalSettings() {
    this.formGroup.get('international').get('ddpConfig').reset();
  }

  fetchExtraFields() {
    this.formService
      .getCarrierTypeShipmentOptionsFields(this.activeCarrier?.id)
      .pipe(first())
      .subscribe((shipmentExtras) => {
        this.shipmentExtrasFields = shipmentExtras?.fields || [];
        this.initShipmentExtrasFields(this.shipmentExtrasValues);
      });
  }

  initProvidersValues() {
    if (this.loadedCarrier) {
      this.selectedActiveProviders = this.shipmentExtrasValues.activeServiceProviders || [];
      this.activeProviderServicesIds = this.shipmentExtrasValues.activeProviderServiceLevelsIds || [];
    } else {
      this.selectedActiveProviders = this.activeCarrier.providers.flatMap((p) =>
        p.enabled ? p.name?.toLowerCase() : [],
      );
      this.activeProviderServicesIds = this.activeCarrier.providers.flatMap((p) =>
        p.providerServices.flatMap((ps) => (ps.enabled ? ps.id + '' : [])),
      );
    }

    this.setShipmentExtrasField('activeServiceProviders', this.selectedActiveProviders ?? undefined);
    this.setShipmentExtrasField('activeProviderServiceLevelsIds', this.activeProviderServicesIds ?? undefined);
  }

  // manage provider service levels
  onProviderServiceSelect(value: any, providerService: CarrierProviderServices) {
    if (!providerService) {
      return;
    }

    if (value) {
      this.activeProviderServicesIds.push(providerService.id + '');
    }

    if (!value) {
      const providerIndex = this.activeProviderServicesIds.findIndex((id) => id == providerService.id + '');
      this.activeProviderServicesIds.splice(providerIndex, 1);
    }

    this.setShipmentExtrasField(
      'activeProviderServiceLevelsIds',
      Array.from(new Set(this.activeProviderServicesIds)) ?? undefined,
    );
  }

  // manage provider
  onProviderSelect(value: any, provider: CarrierProviders) {
    if (!provider) {
      return;
    }

    if (value) {
      this.selectedActiveProviders.push(provider.name?.toLowerCase() as CarrierProvidersNames);
    }

    if (!value) {
      const index: number = this.selectedActiveProviders.findIndex((value) => value === provider.name?.toLowerCase());
      this.selectedActiveProviders.splice(index, 1);
    }

    this.setShipmentExtrasField('activeServiceProviders', this.selectedActiveProviders ?? undefined);
  }

  initShipmentExtrasFields(shipmentExtrasValues?: ShipmentExtras) {
    if (!isNaN(shipmentExtrasValues?.id)) {
      this.formGroup.addControl('shipmentExtrasId', new UntypedFormControl(shipmentExtrasValues.id));
    }

    this.shipmentExtrasFields?.forEach((field) => {
      if (shipmentExtrasValues) {
        this.shipmentExtrasFormGroup.addControl(
          field.fieldName,
          new UntypedFormControl(shipmentExtrasValues[field.fieldName]),
        );
      } else {
        this.shipmentExtrasFormGroup.addControl(field.fieldName, new UntypedFormControl(field.defaultValue));
      }
    });
    this.shipmentExtrasFormGroup.addControl('activeServiceProviders', new UntypedFormControl([]));
    this.shipmentExtrasFormGroup.addControl('activeProviderServiceLevelsIds', new UntypedFormControl([]));

    this.initProvidersValues();
    if (this.loadedCarrier) this.shipmentExtrasLoaded.emit();
  }

  findDefaultOption(options: Options[], control: any): DropdownOption {
    if (!control) {
      return;
    }

    return options.find((opt) => opt.value === control.value) as DropdownOption;
  }

  setShipmentExtrasField(control: string, value: any): any {
    return this.shipmentExtrasFormGroup['controls'][control]?.setValue(value);
  }

  getProviderDisplayNameMap(provider: { name: string }) {
    const providerDisplayName = carrierProviderDisplayNameMap[provider.name]?.toLowerCase() || provider.name;
    return providerDisplayName;
  }

  onArrowClick(event, index: number) {
    this.arrowFunctionArray[index] = !this.arrowFunctionArray[index];
  }
}
